import { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Modal,
  Backdrop,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { useTranslation } from "react-i18next";
import BasicInfoBox from "./BasicInfoBox.jsx";
import StatisticsOfUserBox from "./StatisticsOfUserBox.jsx";
import { ChatButton, ChatPanel } from "../../Chat";
import { getUserReportData, getLocalStorageValues, getSleepDataBuSubjectID, getExerciseDataBySubjectID } from "../../../services/apiCalls.js";
import { generateUserReport } from "../../../services/pdfGenerator/generateUserReport.js";
const style = {
  display: "flex",
  flexDirection: "column",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "85vw",
  bgcolor: "#ececec",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  maxHeight: "90vh",
  overflowY: "auto",
};

export default function UserInfoModal({ open, setOpen, selectedUser }) {
  // console.log("Selected user in userinfomodal: ", selectedUser);
  const [loading, setLoading] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const { t } = useTranslation("statistics");
  const { t: tExport } = useTranslation("export");
  useEffect(() => {
    localStorage.setItem("userSelectedId", selectedUser?.id);
  }, [selectedUser]);

  const handleClose = () => {
    setOpen(false);
    setChatOpen(false);
  };

  const handleDownloadReport = async () => {
    if (!selectedUser?.id) return;

    setDownloadingReport(true);
    try {
      // Fetch all data in parallel
      const [reportData, sleepData, exerciseData] = await Promise.all([
        getUserReportData(selectedUser.id),
        getSleepDataBuSubjectID(selectedUser.subject_id),
        getExerciseDataBySubjectID(selectedUser.subject_id),
      ]);

      const { clinician_name } = getLocalStorageValues();
      await generateUserReport(
        reportData,
        clinician_name,
        sleepData || [],
        exerciseData || []
      );
    } catch (error) {
      console.error("Error generating report:", error);
    } finally {
      setDownloadingReport(false);
    }
  };
  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        <Box sx={{ width: "100%", height: "100%" }}>
          <Box sx={{ ...style }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Tooltip title={tExport("report.downloadReport")}>
                <IconButton
                  onClick={handleDownloadReport}
                  disabled={downloadingReport}
                  sx={{ color: "primary.main" }}
                >
                  {downloadingReport ? (
                    <CircularProgress size={24} />
                  ) : (
                    <DownloadIcon />
                  )}
                </IconButton>
              </Tooltip>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography
              variant="h5"
              id="parent-modal-title"
              sx={{ textAlign: "center" }}
            >
              {t("subjectOverview")}
            </Typography>
            <BasicInfoBox selectedUser={selectedUser} />
            <StatisticsOfUserBox
              selectedUser={selectedUser}
              setLoading={setLoading}
            />
          </Box>
          {/* Chat Button */}
          <ChatButton onClick={() => setChatOpen(true)} />
          {/* Chat Panel */}
          <ChatPanel
            open={chatOpen}
            onClose={() => setChatOpen(false)}
            subjectId={selectedUser?.subject_id}
          />
        </Box>
      </Modal>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: 9999,
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}
